import { memo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import CategoryIcon from "@mui/icons-material/Category";
import { riskLevelColor } from "@/lib/listData";
import ModernCard from "./ModernCard";
import PropTypes from "prop-types";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";

// Helper function to get risk level key based on the risk matrix
const getRiskLevelKey = (likelihood, severity) => {
  if (likelihood === 1) {
    if (severity <= 2) return "Low";
    return "Moderate";
  } else if (likelihood === 2) {
    if (severity === 1) return "Low";
    if (severity <= 3) return "Moderate";
    return "Critical";
  } else if (likelihood === 3) {
    if (severity <= 2) return "Moderate";
    if (severity <= 4) return "Critical";
    return "Intolerable";
  } else if (likelihood === 4) {
    if (severity === 1) return "Moderate";
    if (severity <= 3) return "Critical";
    return "Intolerable";
  } else if (likelihood === 5) {
    if (severity === 1) return "Moderate";
    if (severity === 2) return "Critical";
    return "Intolerable";
  }
  return "Low"; // Default fallback
};

const HazardsWithRiskLevels = memo(
  ({ hazards, documentCreatedAt, t, theme }) => {
    const { token } = useSession();
    const [expandedHazard, setExpandedHazard] = useState(null);
    const [precautionCategoryMap, setPrecautionCategoryMap] = useState({});
    const [precautionsMetadata, setPrecautionsMetadata] = useState([]);
    const [isLoadingPrecautions, setIsLoadingPrecautions] = useState(false);

    // Fetch precaution tree to map precautions to categories
    useEffect(() => {
      const fetchPrecautionTree = async () => {
        setIsLoadingPrecautions(true);
        try {
          const response = await axios.get("/api/v1/metadata/precaution-tree", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const precautionTree = response.data || [];

          // Build map from precaution ID to category info
          const categoryMap = {};
          precautionTree.forEach((category) => {
            if (category.children && Array.isArray(category.children)) {
              category.children.forEach((precaution) => {
                categoryMap[precaution.id] = {
                  categoryId: category.category_id,
                  categoryName: category.category_name,
                };
              });
            }
          });
          setPrecautionCategoryMap(categoryMap);
        } catch (error) {
          console.error("Error fetching precaution tree:", error);
        } finally {
          setIsLoadingPrecautions(false);
        }
      };

      if (token && hazards && hazards.length > 0) {
        fetchPrecautionTree();
      }
    }, [token, hazards]);

    // Fetch precautions metadata to get created_at dates
    useEffect(() => {
      const fetchPrecautionsMetadata = async () => {
        try {
          const response = await axios.get("/api/v1/metadata/precautions", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setPrecautionsMetadata(response.data || []);
        } catch (error) {
          console.error("Error fetching precautions metadata:", error);
        }
      };

      if (token && documentCreatedAt) {
        fetchPrecautionsMetadata();
      }
    }, [token, documentCreatedAt]);

    const handleAccordionChange = (hazardId) => (event, isExpanded) => {
      setExpandedHazard(isExpanded ? hazardId : null);
    };

    // Helper function to check if precaution was added after document creation
    const getPrecautionAddedDate = (precautionId) => {
      if (!documentCreatedAt || !precautionsMetadata.length) return null;

      const precaution = precautionsMetadata.find((p) => p.id === precautionId);
      if (!precaution || !precaution.created_at) return null;

      const docCreatedDate = new Date(documentCreatedAt);
      const precautionCreatedDate = new Date(precaution.created_at);

      // If precaution was created after document, return formatted date with time
      if (precautionCreatedDate > docCreatedDate) {
        // Format as MM/DD/YYYY HH:MM
        const month = String(precautionCreatedDate.getMonth() + 1).padStart(
          2,
          "0"
        );
        const day = String(precautionCreatedDate.getDate()).padStart(2, "0");
        const year = precautionCreatedDate.getFullYear();
        const hours = String(precautionCreatedDate.getHours()).padStart(2, "0");
        const minutes = String(precautionCreatedDate.getMinutes()).padStart(
          2,
          "0"
        );
        return `${month}/${day}/${year} ${hours}:${minutes}`;
      }

      return null;
    };

    // Group precautions by category
    const getCategorizedPrecautions = (hazard) => {
      if (!hazard.precautions || hazard.precautions.length === 0) {
        return [];
      }

      const categoriesMap = {};
      const uncategorizedPrecautions = [];

      hazard.precautions.forEach((precaution) => {
        const categoryInfo = precautionCategoryMap[precaution.id];

        if (categoryInfo) {
          const { categoryId, categoryName } = categoryInfo;
          if (!categoriesMap[categoryId]) {
            categoriesMap[categoryId] = {
              id: categoryId,
              name: categoryName,
              precautions: [],
            };
          }
          categoriesMap[categoryId].precautions.push(precaution);
        } else {
          // Precaution has no category in metadata
          uncategorizedPrecautions.push(precaution);
        }
      });

      // Convert map to array and add "Other" category if there are uncategorized precautions
      const categories = Object.values(categoriesMap);

      if (uncategorizedPrecautions.length > 0) {
        categories.push({
          id: "other",
          name: t("documentDetail.otherCategory", "Other"),
          precautions: uncategorizedPrecautions,
          isOther: true,
        });
      }

      return categories;
    };

    const getRiskLevelStyle = (likelihood, severity) => {
      return (
        riskLevelColor[likelihood]?.[severity] || {
          backgroundColor: "transparent",
          color: "text.primary",
        }
      );
    };

    if (!hazards || hazards.length === 0) {
      return (
        <ModernCard
          title={t("documentDetail.hazardsTitle")}
          icon={WarningIcon}
          color="error"
        >
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t("documentDetail.noDataAvailable")}
            </Typography>
          </Box>
        </ModernCard>
      );
    }

    return (
      <ModernCard
        title={t("documentDetail.hazardsTitle")}
        icon={WarningIcon}
        color="error"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {hazards.map((hazard) => (
            <Accordion
              key={hazard.id}
              expanded={expandedHazard === hazard.id}
              onChange={handleAccordionChange(hazard.id)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: "background.paper",
                  "&.Mui-expanded": {
                    minHeight: 48,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                    "&.Mui-expanded": {
                      margin: "12px 0",
                    },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: expandedHazard === hazard.id ? 600 : 400,
                  }}
                >
                  {hazard.name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  borderTop: "1px solid",
                  borderColor: "divider",
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Precautions Section */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ mb: 1.5 }}
                    >
                      {t("documentDetail.precautionsTitle", "Precautions")}
                    </Typography>
                    {isLoadingPrecautions ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {getCategorizedPrecautions(hazard).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              "documentDetail.noPrecautions",
                              "No precautions available"
                            )}
                          </Typography>
                        ) : (
                          getCategorizedPrecautions(hazard).map((category) => (
                            <Box key={category.id}>
                              {/* Category Header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: alpha(
                                    category.isOther
                                      ? theme.palette.warning.main
                                      : theme.palette.success.main,
                                    0.08
                                  ),
                                }}
                              >
                                <CategoryIcon
                                  sx={{
                                    fontSize: 16,
                                    color: category.isOther
                                      ? "warning.main"
                                      : "success.main",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight="600"
                                  color={
                                    category.isOther
                                      ? "warning.main"
                                      : "success.main"
                                  }
                                >
                                  {category.name}
                                </Typography>
                              </Box>
                              {/* Precautions as checkboxes */}
                              <Box
                                sx={{
                                  pl: 2,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                {category.precautions?.map((precaution) => {
                                  const isSelected = precaution.selected;
                                  const addedDate = !isSelected
                                    ? getPrecautionAddedDate(precaution.id)
                                    : null;

                                  return (
                                    <FormControlLabel
                                      key={precaution.id}
                                      control={
                                        <Checkbox
                                          checked={isSelected}
                                          disabled
                                          size="small"
                                          sx={{
                                            color: alpha(
                                              theme.palette.info.main,
                                              0.5
                                            ),
                                            "&.Mui-checked": {
                                              color: category.isOther
                                                ? "warning.main"
                                                : "success.main",
                                            },
                                            "&.Mui-disabled": {
                                              color: isSelected
                                                ? category.isOther
                                                  ? "warning.main"
                                                  : "success.main"
                                                : alpha(
                                                    theme.palette.text.disabled,
                                                    0.3
                                                  ),
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography
                                          variant="body2"
                                          color={
                                            isSelected
                                              ? "text.primary"
                                              : "text.secondary"
                                          }
                                          sx={{
                                            fontWeight: isSelected ? 500 : 400,
                                            ...(category.isOther &&
                                              isSelected && {
                                                textDecoration: "underline",
                                                textDecorationColor: alpha(
                                                  theme.palette.warning.main,
                                                  0.6
                                                ),
                                                textDecorationThickness: "2px",
                                                textUnderlineOffset: "3px",
                                              }),
                                          }}
                                        >
                                          {precaution.name}
                                          {addedDate && (
                                            <Typography
                                              component="span"
                                              variant="caption"
                                              sx={{
                                                ml: 1,
                                                fontStyle: "italic",
                                                color: "info.main",
                                              }}
                                            >
                                              {t("documentDetail.addedOn", {
                                                date: addedDate,
                                              })}
                                            </Typography>
                                          )}
                                        </Typography>
                                      }
                                      sx={{
                                        mb: 0.5,
                                        alignItems: "center",
                                        display: "flex",
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          ))
                        )}
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Risk Levels Section */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ mb: 1.5 }}
                    >
                      {t("documentDetail.riskLevelsTitle")}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 2,
                      }}
                    >
                      {/* People Risk Level */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                        >
                          {t("documentDetail.riskLevelPeople", "People:")}
                        </Typography>
                        <Chip
                          label={t(
                            `riskMatrix.riskLevel${getRiskLevelKey(hazard.likelihood, hazard.severity_people)}`
                          )}
                          size="small"
                          sx={{
                            bgcolor: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_people
                            ).backgroundColor,
                            color: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_people
                            ).color,
                            fontWeight: "bold",
                            width: "fit-content",
                          }}
                        />
                      </Box>

                      {/* Environment Risk Level */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                        >
                          {t(
                            "documentDetail.riskLevelEnvironment",
                            "Environment:"
                          )}
                        </Typography>
                        <Chip
                          label={t(
                            `riskMatrix.riskLevel${getRiskLevelKey(hazard.likelihood, hazard.severity_environment)}`
                          )}
                          size="small"
                          sx={{
                            bgcolor: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_environment
                            ).backgroundColor,
                            color: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_environment
                            ).color,
                            fontWeight: "bold",
                            width: "fit-content",
                          }}
                        />
                      </Box>

                      {/* Asset Risk Level */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                        >
                          {t("documentDetail.riskLevelAsset", "Asset:")}
                        </Typography>
                        <Chip
                          label={t(
                            `riskMatrix.riskLevel${getRiskLevelKey(hazard.likelihood, hazard.severity_asset)}`
                          )}
                          size="small"
                          sx={{
                            bgcolor: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_asset
                            ).backgroundColor,
                            color: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_asset
                            ).color,
                            fontWeight: "bold",
                            width: "fit-content",
                          }}
                        />
                      </Box>

                      {/* Reputation Risk Level */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                        >
                          {t(
                            "documentDetail.riskLevelReputation",
                            "Reputation:"
                          )}
                        </Typography>
                        <Chip
                          label={t(
                            `riskMatrix.riskLevel${getRiskLevelKey(hazard.likelihood, hazard.severity_reputation)}`
                          )}
                          size="small"
                          sx={{
                            bgcolor: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_reputation
                            ).backgroundColor,
                            color: getRiskLevelStyle(
                              hazard.likelihood,
                              hazard.severity_reputation
                            ).color,
                            fontWeight: "bold",
                            width: "fit-content",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </ModernCard>
    );
  }
);

HazardsWithRiskLevels.displayName = "HazardsWithRiskLevels";

HazardsWithRiskLevels.propTypes = {
  hazards: PropTypes.array,
  documentCreatedAt: PropTypes.string,
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

export default HazardsWithRiskLevels;
